import type {SvgIconComponent} from "@mui/icons-material";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import {Avatar, ButtonBase, Link, Stack} from "@mui/material";
import {alpha} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import ProfileAvatar from "crqlar-react-common/dist/components/profileIdentity/ProfileAvatar";
import {LanguageChip} from "crqlar-react-common/dist/components/profileIdentity/ProfileLanguage";
import {getCompletedStays, getDisplayName} from "crqlar-react-common/dist/components/profileIdentity/profileIdentity.util";
import ProfilePreviewDialog from "crqlar-react-common/dist/components/profilePreview/ProfilePreviewDialog";
import ProfileHeaderChips from "crqlar-react-common/dist/components/profilePreview/sidebar/ProfileHeaderChips";
import {getSectorIcon} from "crqlar-react-common/dist/components/sectorIcons/sectorIcons";
import {ElementChip} from "crqlar-react-common/dist/components/uiElements/base/ElementChip";
import {ElementChipList} from "crqlar-react-common/dist/components/uiElements/base/ElementChipList";
import {getStayStatusDisplay} from "crqlar-react-common/dist/components/uiElements/StayStatusElement";
import type {
    AggregationSurveyReplyGuest,
    AggregationSurveySubmissionListItem
} from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import type {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {type Reservation, ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {getGuestStayStatusInfo} from "crqlar-react-common/dist/utils/color.util";
import {convertLocalDateToDate, formatFullDMY} from "crqlar-react-common/dist/utils/date.util";
import {useProfilesQuery} from "crqlar-react-common/dist/utils/hooks/profile/useProfiles";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {birthdayToAge, formatPhoneForDisplay} from "crqlar-react-common/dist/utils/profile.util";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import React, {useEffect, useState} from "react";
import ReservationDialog from "../../reservations/reservationDialog/ReservationDialog";
import {formatDayRange, formatReplyStayDates, hasReplyStay} from "../shared/surveyReplyStay.util";
import type {SurveySubmissionGuest} from "./export/useSurveySubmissionGuest";

interface Props {
    submission: AggregationSurveySubmissionListItem
    /** The dialog waits for this lookup before revealing the panel. Exports use the same data. */
    guest: SurveySubmissionGuest
}

const CHIP_ICON_SX = {fontSize: 15, color: 'text.secondary'};

// The primary entry first; hidden ones are the PMS's way of saying "do not use".
const firstVisible = <T extends {hidden?: boolean, isPrimary?: boolean}>(items: T[] | undefined, has: (item: T) => boolean) =>
    [...(items ?? [])]
        .filter(item => has(item) && !item.hidden)
        .sort((a, b) => Number(!!b.isPrimary) - Number(!!a.isPrimary))[0];

// Plain text on purpose: no tel:/mailto:, staff reach guests from their own phone and mail client.
const ContactLine = ({icon, text}: {icon: React.ReactNode, text: string}) => (
    <Stack direction={'row'} alignItems={'center'} gap={1} sx={{minWidth: 0}}>
        {icon}
        <Typography variant={'body1'} sx={{overflowWrap: 'anywhere'}}>
            {text}
        </Typography>
    </Stack>
);

// The stay now or ahead as one chip with its days written out; the sidebar's status chip keeps them in a tooltip.
const StayStatusChip = ({profile}: {profile: Profile}) => {
    const {t} = useLocalize();

    const statusInfo = getGuestStayStatusInfo(profile);
    const display = getStayStatusDisplay(statusInfo?.status);
    // The nearest booked one, as the sidebar's stay card picks it: the API sends upcoming stays in no set order.
    const stay = statusInfo?.status === 'UPCOMING'
        ? [...(profile.upcomingStays ?? [])]
            .filter(upcoming => upcoming.status !== ReservationStatus.PENDING)
            .sort((a, b) => (a.startDay ?? '').localeCompare(b.startDay ?? ''))[0]
        : statusInfo?.stay;
    if (!display || !stay) return null;

    return (
        <ElementChip
            text={[t(display.label), formatDayRange(stay.startDay, stay.endDay, {withYear: false})].filter(Boolean).join(' · ')}
            // The chip drops the current year; hovering always names it.
            tooltipContent={[t(display.label), formatDayRange(stay.startDay, stay.endDay)].filter(Boolean).join(' · ')}
            startIcon={<display.Icon sx={{fontSize: 15, color: display.color}}/>}
            borderColor={alpha(display.color, 0.34)}
            backgroundColor={alpha(display.color, 0.06)}
        />
    );
};

// The sidebar header's avatar and chips, with this panel's own chips in the same row. The name opens the profile.
const GuestHeader = ({profile, onOpen}: {profile: Profile, onOpen: () => void}) => {
    const {t, language} = useLocalize();

    const name = getDisplayName(profile);
    const completedStays = getCompletedStays(profile);
    const birthday = profile.birthDay ? convertLocalDateToDate(profile.birthDay) : undefined;
    const age = profile.birthDay ? birthdayToAge(profile.birthDay) : undefined;
    const hasAge = age !== undefined && Number.isFinite(age) && age >= 0;

    return (
        <Stack direction={'row'} alignItems={'flex-start'} gap={'14px'} sx={{minWidth: 0}}>
            <ProfileAvatar
                profile={profile}
                size={'large'}
                completedStays={completedStays}
                completedStaysLabel={t('{{count}} stays', {count: completedStays})}
            />

            <Stack gap={'8px'} sx={{flex: 1, minWidth: 0, pt: '4px'}}>
                <Link
                    component={'button'}
                    type={'button'}
                    underline={'hover'}
                    onClick={onOpen}
                    aria-label={`${t('Open profile')}: ${name}`}
                    data-dashboard-control
                    sx={{alignSelf: 'flex-start', textAlign: 'left', fontSize: 24, fontWeight: 600, lineHeight: 1.15, overflowWrap: 'anywhere'}}
                >
                    {name}
                </Link>

                <ElementChipList>
                    <ProfileHeaderChips profile={profile}/>
                    {hasAge && (
                        <ElementChip
                            text={t('{{count}} years', {count: age})}
                            tooltipContent={birthday ? formatFullDMY(birthday) : undefined}
                            startIcon={<CakeOutlinedIcon sx={CHIP_ICON_SX}/>}
                        />
                    )}
                    {!!profile.languageCode && <LanguageChip code={profile.languageCode} locale={language}/>}
                    <StayStatusChip profile={profile}/>
                </ElementChipList>
            </Stack>
        </Stack>
    );
};

// A reply without a profile behind it has no avatar ring or stays to show.
const AnonymousHeader = ({name, initials}: {name: string, initials: string}) => (
    <Stack direction={'row'} alignItems={'center'} gap={1.5} sx={{minWidth: 0}}>
        <Avatar
            sx={theme => ({
                width: 48,
                height: 48,
                flexShrink: 0,
                borderRadius: '8px',
                fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main',
            })}
        >
            {initials || <PersonOutlineIcon/>}
        </Avatar>

        <Typography variant={'h6'} fontWeight={600} sx={{lineHeight: 1.35, overflowWrap: 'anywhere'}}>
            {name}
        </Typography>
    </Stack>
);

// Until a guest's profile arrives, the name gives the avatar's initials; the ring takes its colour once it is there.
const nameAsProfile = (name?: string | null): Pick<Profile, 'firstName' | 'lastName'> => {
    const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
    return {firstName: parts[0], lastName: parts.length > 1 ? parts[parts.length - 1] : undefined};
};

interface ReplyGuestsProps {
    mainGuests: AggregationSurveyReplyGuest[]
    fellowGuests: AggregationSurveyReplyGuest[]
    profiles?: Profile[]
}

/*
    The stay's other guests the way the reservation dialog shows them - the small avatar and the name - but without
    chips: the whole box is one button that opens the booking. A fellow guest's reply lists the main guests first.
    "With" leads them, as on the printout's booking line.
*/
const ReplyGuests = ({mainGuests, fellowGuests, profiles}: ReplyGuestsProps) => {
    const {t} = useLocalize();

    const guestEntry = (entry: AggregationSurveyReplyGuest, primary: boolean) => (
        <Stack key={entry.id} direction={'row'} alignItems={'center'} gap={0.75} sx={{minWidth: 0}}>
            <ProfileAvatar profile={profiles?.find(profile => profile.id === entry.id) ?? nameAsProfile(entry.name)} size={'xs'}/>
            <Typography variant={'body2'} sx={{overflowWrap: 'anywhere'}}>
                {entry.name?.trim() || t('Guest')}
                {primary && (
                    <Typography component={'span'} variant={'body2'} color={'text.secondary'}>
                        {` · ${t('Primary guest')}`}
                    </Typography>
                )}
            </Typography>
        </Stack>
    );

    // A wrapped second row of guests lines up under the first guest, not under "With". Its 20px line is the xs
    // avatar's height, so "With" centres on the first row like the names do.
    return (
        <Stack direction={'row'} alignItems={'flex-start'} gap={1} sx={{minWidth: 0}}>
            <Typography variant={'body2'} color={'text.secondary'} sx={{flexShrink: 0, lineHeight: '20px'}}>
                {t('With')}
            </Typography>
            <Stack direction={'row'} flexWrap={'wrap'} columnGap={1.75} rowGap={0.75} sx={{minWidth: 0}}>
                {mainGuests.map(entry => guestEntry(entry, true))}
                {fellowGuests.map(entry => guestEntry(entry, false))}
            </Stack>
        </Stack>
    );
};

interface ReplyRowProps {
    title: string
    detail?: React.ReactNode
    /** The booking's sector icon, the one the profile's visit list shows. */
    icon?: SvgIconComponent
    onOpen: () => void
}

const ReplyRow = ({title, detail, icon: Icon, onOpen}: ReplyRowProps) => {
    const {t} = useLocalize();

    return (
        <ButtonBase
            onClick={onOpen}
            aria-label={`${t('Open reservation')}: ${title}`}
            data-dashboard-control
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 1.25,
                textAlign: 'left',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:hover': {bgcolor: 'action.hover'},
            }}
        >
            {Icon && <Icon sx={{fontSize: 24, color: 'text.secondary', flexShrink: 0}}/>}

            <Stack gap={0.75} sx={{flex: 1, minWidth: 0}}>
                <Typography variant={'body1'} fontWeight={600} sx={{lineHeight: 1.35, overflowWrap: 'anywhere'}}>
                    {title}
                </Typography>
                {typeof detail === 'string'
                    ? (
                        <Typography variant={'body2'} color={'text.secondary'} sx={{overflowWrap: 'anywhere'}}>
                            {detail}
                        </Typography>
                    )
                    : detail}
            </Stack>

            <ChevronRightRoundedIcon sx={{color: 'text.secondary', flexShrink: 0}}/>
        </ButtonBase>
    );
};

/**
 * Who answered and what the reply was about - only what a reviewer needs to decide and follow up: one chip row under
 * the name, the contact, then the reply's booking, labelled like the review form below it. Profile and booking open
 * in place.
 */
const SurveySubmissionGuestCard = ({submission, guest}: Props) => {
    const {t} = useLocalize();

    const [shownProfile, setShownProfile] = useState<Profile>();
    const [shownReservation, setShownReservation] = useState<Reservation>();

    // Moving on to the next reply must not carry the previous guest's dialogs along.
    useEffect(() => {
        setShownProfile(undefined);
        setShownReservation(undefined);
    }, [submission.id]);

    const profile = guest.profile;
    const replyRow = guest.contextRows.find(row => row.kind === 'reservation');
    const phone = firstVisible(profile?.phones, item => !!item.phoneNumberFull?.trim());
    const email = firstVisible(profile?.emails, item => !!item.email?.trim());

    const mainGuests = submission.mainGuests ?? [];
    const fellowGuests = submission.fellowGuests ?? [];
    const {data: guestProfiles} = useProfilesQuery([...mainGuests, ...fellowGuests].map(entry => entry.id));
    const sectorIcon = getSectorIcon(useSector(guest.reservation?.sectorId ?? submission.sectorId));

    return (
        <Stack gap={2}>
            {profile
                ? <GuestHeader profile={profile} onOpen={() => setShownProfile(profile)}/>
                : <AnonymousHeader name={guest.name} initials={guest.initials}/>}

            {!replyRow && !!submission.sectorName && (
                <Typography variant={'body2'} color={'text.secondary'}>
                    {submission.sectorName}
                </Typography>
            )}

            {(!!phone || !!email) && (
                <Stack gap={0.75}>
                    {!!phone && (
                        <ContactLine
                            icon={<PhoneOutlinedIcon sx={{fontSize: 18, color: 'text.secondary'}}/>}
                            text={formatPhoneForDisplay(phone.phoneNumberFull)}
                        />
                    )}
                    {!!email && (
                        <ContactLine
                            icon={<MailOutlineRoundedIcon sx={{fontSize: 18, color: 'text.secondary'}}/>}
                            text={email.email!.trim().toLowerCase()}
                        />
                    )}
                </Stack>
            )}

            {!!replyRow && !!guest.reservation && (
                <Stack gap={0.75}>
                    <Typography variant={'body1'} color={'text.secondary'}>
                        {t('This reply')}
                    </Typography>

                    {hasReplyStay(submission)
                        ? (
                            <ReplyRow
                                icon={sectorIcon}
                                // "Room 360" (or "Stay") and the days, as on the printout's booking line.
                                title={[replyRow.title, formatReplyStayDates(submission)].filter(Boolean).join(' · ')}
                                detail={mainGuests.length || fellowGuests.length
                                    ? <ReplyGuests mainGuests={mainGuests} fellowGuests={fellowGuests} profiles={guestProfiles}/>
                                    : undefined}
                                onOpen={() => setShownReservation(guest.reservation)}
                            />
                        )
                        : (
                            <ReplyRow
                                icon={sectorIcon}
                                title={replyRow.title}
                                detail={replyRow.detail}
                                onOpen={() => setShownReservation(guest.reservation)}
                            />
                        )}
                </Stack>
            )}

            <ProfilePreviewDialog
                open={!!shownProfile}
                onClose={() => setShownProfile(undefined)}
                profile={shownProfile}
                setProfile={setShownProfile}
            />

            <ReservationDialog
                open={!!shownReservation}
                reservation={shownReservation}
                onClose={() => setShownReservation(undefined)}
                onSave={() => setShownReservation(undefined)}
                onChangeReservation={setShownReservation}
            />
        </Stack>
    );
};

export default SurveySubmissionGuestCard;
